import { push } from "connected-react-router";

import * as runReducer from "containers/Run/reducer";
import * as setupReducer from "containers/Setup/reducer";
import * as modelGroupReducer from "containers/ModelGroup/reducer";
import * as homeReducer from "containers/Home/reducer";

import { ConfusionMatrix } from "utils/stats";

export const INIT_OPTIMIZER = "singleOptimizer/INIT_OPTIMIZER";
export const UPDATE_KEY = "singleOptimizer/UPDATE_KEY";
export const UPDATE_SUB_KEY = "singleOptimizer/UPDATE_SUB_KEY";
export const RESET = "singleOptimizer/RESET";

const initialState = {};

function optimizerReducer(state = initialState, action) {
  switch (action.type) {
    case INIT_OPTIMIZER:
      return {
        ...state,
        ...action.data
      };
    case UPDATE_KEY:
      return {
        ...state,
        [action.key]: action.value
      };
    case UPDATE_SUB_KEY:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          [action.subKey]: action.value
        }
      };
    case RESET:
      return {
        ...initialState
      };
    default:
      return state;
  }
}

/* Selectors */
export function getBase(state) {
  return state.singleOptimizer;
}

export function getOptimizer(state, id) {
  return getBase(state)[id];
}

export function getRunId(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).runId : undefined;
}

export function getOverallScale(state, id) {
  return getOptimizer(state, id).overallScale;
}

export function getOptimizers(state) {
  return Object.keys(state.optimizer).map((o) => state.optimizer[o]);
}

export function getUnitCost(state, id) {
  return Math.abs(getOptimizedConversionCost(state, id));
}

export function getUnitRevenue(state, id) {
  return getOptimizedConversionGain(state, id) + Math.abs(getOptimizedConversionCost(state, id));
}

export function getOptimizedConversionGain(state, runId) {
  return setupReducer.getConversionGain(state, runId, true);
}

export function getOptimizedConversionCost(state, runId) {
  return setupReducer.getConversionCost(state, runId, true);
}

export function getBudget(state, id) {
  return getOptimizer(state, id) ? Number(getOptimizer(state, id).budget) : 0;
}

export function getCapacity(state, optimizerId, runId) {
  return getBudget(state, optimizerId) / getUnitCost(state, runId ? runId : optimizerId);
}

export function getTotalRevenue(state, optimizerId, runId) {
  return getTotalWon(state, optimizerId, runId) * getUnitRevenue(state, optimizerId, runId);
}

export function getTotalCost(state, optimizerId, runId) {
  return getTotalPursued(state, optimizerId, runId) * getUnitCost(state, optimizerId, runId);
}

export function getProfit(state, optimizerId, runId) {
  return getTotalRevenue(state, optimizerId, runId) - getTotalCost(state, optimizerId, runId);
}

export function getConfusionMatrix(state, id, isNotScaled = false) {
  const model = runReducer.getFirstModel(state, id);

  let pctOfRows = 1.0;

  const cm = getConfusionMatrixFromModel(model);

  return isNotScaled ? cm : cm.copyAndScaleTotal(cm.getTotal() * pctOfRows);
}

export function getSalesConfusionMatrix(state, id) {
  const runId = getSalesRunId(state, id);
  const salesModel = runReducer.getFirstModel(state, runId);

  return getConfusionMatrixFromModel(salesModel);
}

function getConfusionMatrixFromModel(model) {
  const { truePositive, falsePositive, falseNegative, trueNegative } = model.maxIteration;

  return new ConfusionMatrix(truePositive, trueNegative, falsePositive, falseNegative);
}

export function getConstrainedConfusionMatrix(state, optimizerId, runId) {
  const capacity = getCapacity(state, optimizerId, runId);

  return getConfusionMatrix(state, runId ? runId : optimizerId).copyAndConstrainPositivePredictions(capacity);
}

export function getTotalProspects(state, optimizerId, runId) {
  return getConfusionMatrix(state, runId).getTotal();
}

export function getTotalPursued(state, optimizerId, runId) {
  return getConstrainedConfusionMatrix(state, optimizerId, runId).getAllPositive();
}

export function getTotalWon(state, optimizerId, runId) {
  return getConstrainedConfusionMatrix(state, optimizerId, runId).getTruePositive();
}

export function getDrilldownColumn(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).drilldownColumn : undefined;
}

export function getDrilldownValue(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).drilldownValue : undefined;
}

export function getSalesDrilldown(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).salesDrilldown : undefined;
}

export function getMarketingDrilldown(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).marketingDrilldown : undefined;
}

export function getBreakEvenProbability(state, id) {
  const salesRunId = getSalesRunId(state, id);
  const conversionCost = getOptimizedConversionCost(state, salesRunId);
  const conversionGain = getOptimizedConversionGain(state, salesRunId);
  const processLimitPct = setupReducer.getProcessLimitPct(state, salesRunId);

  const breakEvenProb = -conversionCost / (-conversionCost + conversionGain);

  const retBreakEvenProb = breakEvenProb + (1 - breakEvenProb) * (1 - processLimitPct);

  return retBreakEvenProb;
}

export function getCurrentProfit(state, id) {
  return getCurrentRevenue(state, id) - getCurrentConfusionMatrix(state, id).getAllPositive() * getUnitCost(state, id);
}

export function getCurrentRevenue(state, id) {
  return getCurrentConfusionMatrix(state, id).getTruePositive() * getUnitRevenue(state, id);
}

export function getCurrentConfusionMatrix(state, runId) {
  return new ConfusionMatrix(
    setupReducer.getTruePositive(state, runId),
    setupReducer.getTrueNegative(state, runId),
    setupReducer.getFalsePositive(state, runId),
    setupReducer.getFalseNegative(state, runId)
  );
}

export function getErrorRuns(state, id) {
  const optimizer = getOptimizer(state, id);

  if (optimizer === undefined) {
    return undefined;
  }

  let errorRuns = {};

  const jobKeys = getAllJobsFromOptimizer(state, optimizer.firstRunId, optimizer.secondRunIde);

  jobKeys.forEach((k) => {
    const job = runReducer.getJob(state, k);

    errorRuns[k] = job ? job.status : undefined;
  });

  return errorRuns;
}

export function getGroupInfo(state, id) {
  const optimizer = getOptimizer(state, id);

  if (optimizer === undefined) {
    return undefined;
  }

  let retInfo = {
    marketing: {},
    sales: {}
  };

  return retInfo;
}

export function getModelGroups(state, optimizerId) {
  let modelGroups = { ...modelGroupReducer.getModelGroup(state, optimizerId) };

  const runId = getRunId(state, optimizerId);

  if (runId === undefined) {
    return undefined;
  }

  modelGroups.hasGroup = true;

  modelGroups.drilldownValues = [
    {
      value: "Overall",
      label: "Overall",
      column: "Overall",
      runId: optimizerId,
      pctOfRows: 1
    }
  ];

  modelGroups.models &&
    Object.keys(modelGroups.models).forEach((m) => {
      if (Object.keys(runReducer.getJob(state, modelGroups.models[m].job_id)).length > 0) {
        modelGroups.models[m] &&
          modelGroups.models[m].conditions &&
          modelGroups.models[m].conditions.length === 1 &&
          modelGroups.drilldownValues.push({
            column: modelGroups.models[m].conditions[0].column,
            value: modelGroups.models[m].conditions[0].value,
            label: modelGroups.models[m].conditions[0].value,
            runId: modelGroups.models[m].job_id,
            totalCost:
              getTotalPursued(state, optimizerId, modelGroups.models[m].job_id) *
              getUnitCost(state, modelGroups.models[m].job_id),
            currentSpend:
              getCurrentConfusionMatrix(state, modelGroups.models[m].job_id).getAllPositive() *
              getUnitCost(state, modelGroups.models[m].job_id)
          });
      }
    });

  modelGroups.drilldownValues.forEach((d) => {
    d.setup = setupReducer.getSetup(state, d.runId, true);
    d.pctOfRows = runReducer.getPctOfRows(state, d.runId);
  });

  modelGroups.topDrivers = modelGroups.drilldownValues.map((v) => ({
    drilldownValue: v.value,
    drilldownColumn: v.column,
    driver: getTopDriver(state, v.runId)
  }));

  modelGroups.overspendCount = modelGroups.drilldownValues.filter((v) => v.totalCost < v.currentSpend).length;
  modelGroups.underspendCount = modelGroups.drilldownValues.filter((v) => v.totalCost > v.currentSpend).length;
  modelGroups.noIssueCount = modelGroups.drilldownValues.filter(
    (v) => v.totalCost === v.currentSpend && v.totalCost !== undefined
  ).length;

  modelGroups.drilldownValues = modelGroups.drilldownValues.sort((a, b) =>
    a.column === "Overall" ? 1 : b.column < a.column ? 1 : -1
  );

  return modelGroups;
}

export function getTopDriver(state, runId) {
  const firstModel = runReducer.getFirstModel(state, runId);

  if (firstModel === undefined || firstModel.features === undefined || firstModel.features.percentages === undefined) {
    return undefined;
  }

  const setup = setupReducer.getSetup(state, runId, true);

  const profit =
    firstModel.maxIteration.truePositive * setup.conversionGain -
    firstModel.maxIteration.falsePositive * setup.conversionCost;

  let topFeature = undefined;
  let topFeatureName = undefined;

  let retFeatures = [];

  Object.keys(firstModel.features.percentages).forEach((p) =>
    Object.keys(firstModel.features.percentages[p].values).forEach((v) => {
      retFeatures.push({
        name: p + ": " + v,
        value: firstModel.features.percentages[p].values[v],
        profit: profit,
        score: Math.abs(profit * firstModel.features.percentages[p].values[v])
      });
    })
  );

  return retFeatures.sort((a, b) => b.score - a.score);
}

export function handleCreateDrilldown(id, drilldownColumn) {
  return (dispatch, getState) => {
    const state = getState();

    const firstRunId = getMarketingRunId(state, id);
    const secondRunId = getSalesRunId(state, id);

    const firstRun = runReducer.getJob(state, firstRunId);
    const secondRun = runReducer.getJob(state, secondRunId);

    const firstDataset = homeReducer.getDataset(state, firstRun.datasetId);
    const secondDataset = homeReducer.getDataset(state, secondRun.datasetId);

    const retModelGroups = {
      models: firstDataset.fields[drilldownColumn.value].data.map((c) => ({
        job_id: null,
        model_name: null,
        conditions: [{ column: drilldownColumn.value, value: c.value, operand: "=" }]
      }))
    };

    /*
    const retModelGroups = {
      models: [
        {
          job_id: null,
          model_name: null,
          conditions: specialVals
            ? specialVals.map(s => ({ column: s.field, value: s.fieldValue, operand: "=" }))
            : []
        },
        ...modelGroup.models
      ]
    };

    return ModelGroupAPI.patchModelGroup(modelGroup.id, retModelGroups).then(response =>
      dispatch(updateModelGroup(originalId, transformModelGroup(response)))
    );
    */
  };
}

export function updateDrilldownValue(id, runId, value) {
  return (dispatch, getState) => {
    dispatch(setRunId(id, runId));
    dispatch(setDrilldownValue(id, value));
  };
}

export function getOptimized(state, id) {
  if (hasLoadedAllJobs(state, id) === false || getOptimizer(state, id) === undefined) {
    return undefined;
  }

  const runId = getRunId(state, id);

  return {
    label: "Marketing",
    id: runId,
    unitCost: getUnitCost(state, runId),
    budget: getBudget(state, id),
    capacity: getCapacity(state, id, runId),
    confusionMatrix: getConfusionMatrix(state, runId),
    constrainedConfusionMatrix: getConstrainedConfusionMatrix(state, id, runId),
    totalWon: getTotalWon(state, id, runId),
    totalPursued: getTotalPursued(state, id, runId),
    totalProspects: getTotalProspects(state, id, runId),
    totalCost: getTotalPursued(state, id, runId) * getUnitCost(state, runId),
    bestModel: runReducer.getFirstModel(state, runId),
    currentSpend: getCurrentConfusionMatrix(state, runId).getAllPositive() * getUnitCost(state, runId),
    setup: setupReducer.getSetup(state, runId, true),
    dataset: setupReducer.getDatasetFromSetup(state, runId),
    profit: getProfit(state, id, runId),
    revenue: getTotalRevenue(state, id, runId),

    currentRevenue: getCurrentRevenue(state, runId),
    revenueDelta: getTotalRevenue(state, id, runId) - getCurrentRevenue(state, runId),

    currentProfit: getCurrentProfit(state, runId),
    profitDelta: getProfit(state, id, runId) - getCurrentProfit(state, runId)
  };
}

/* Actions */

export function updateBase(base) {}

export function setOptimizer(id, optimizer) {
  return (dispatch, getState) => {
    dispatch({ type: UPDATE_KEY, key: id, value: optimizer });
  };
}

export function setBudget(id, budget) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "budget",
    value: budget
  };
}

export function setRunId(id, runId) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_SUB_KEY,
      key: id,
      subKey: "runId",
      value: runId
    });
  };
}

export function setDrilldownValue(id, drilldownValue) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "drilldownValue",
    value: drilldownValue
  };
}

export function setDrilldownColumn(id, drilldownColumn) {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_SUB_KEY,
      key: id,
      subKey: "drilldownColumn",
      value: drilldownColumn
    });

    return dispatch(initOptimizerObject(id));
  };
}

export function setSalesDrilldown(id, salesDrilldown) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "salesDrilldown",
    value: salesDrilldown
  };
}

export function setMarketingDrilldown(id, marketingDrilldown) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "marketingDrilldown",
    value: marketingDrilldown
  };
}

export function setOverallScaleFromRowCount(id, rowCount) {
  return (dispatch, getState) => {
    const cm = getMarketingConfusionMatrix(getState(), id, true);

    const overallScale = Number(rowCount) / Number(cm.getTotal());

    return dispatch(setOverallScale(id, overallScale));
  };
}

/* Action Creators */
export function pollJobs(id) {
  return (dispatch, getState) => {
    const optimizer = getOptimizer(getState(), id);

    const jobKeys = [];

    Object.keys(optimizer.overallRuns).forEach((v) =>
      Object.keys(optimizer.overallRuns[v]).forEach((p) => jobKeys.push(optimizer.overallRuns[v][p]))
    );

    Object.keys(optimizer.combinedRuns).forEach((v) =>
      Object.keys(optimizer.combinedRuns[v]).forEach((p) => jobKeys.push(optimizer.combinedRuns[v][p]))
    );

    Object.keys(optimizer.marketingRuns).forEach((v) => jobKeys.push(optimizer.marketingRuns[v]));

    Object.keys(optimizer.salesRuns).forEach((v) => jobKeys.push(optimizer.salesRuns[v]));

    jobKeys.forEach((k) => {
      if (Object.keys(runReducer.getJob(getState(), k)).length === 0) {
        dispatch(runReducer.pollJob(k, true));
      }
    });
  };
}

export function handleCreate(id, columnOne, columnTwo) {
  return (dispatch, getState) => {
    const state = getState();
    const dataset = setupReducer.getDatasetFromSetup(state, id);

    let modelGroup = modelGroupReducer.getModelGroup(state, id, false);

    if (
      dataset &&
      dataset.fields &&
      dataset.fields[columnOne] &&
      dataset.fields[columnOne].data &&
      dataset &&
      dataset.fields &&
      dataset.fields[columnTwo] &&
      dataset.fields[columnTwo].data
    ) {
      const modelLength =
        dataset.fields[columnOne].data.length +
        dataset.fields[columnTwo].data.length +
        dataset.fields[columnOne].data.length * dataset.fields[columnTwo].data.length;

      dataset.fields[columnOne].data.forEach((c, i) => {
        dispatch(createRunFromValue(id, columnOne, c.value)).then((val) => {
          modelGroup.models.push({
            job_id: val,
            model_name: "",
            conditions: [{ column: columnOne, value: c.value, operand: "=" }]
          });

          if (modelGroup.models.length === modelLength) {
            dispatch(modelGroupReducer.updateModelGroup(id, modelGroup, true));
          }
        });

        dataset.fields[columnTwo].data.forEach((s, i) => {
          dispatch(createRunFromValue(id, columnTwo, s.value)).then((val) => {
            modelGroup.models.push({
              job_id: val,
              model_name: "",
              conditions: [
                { column: columnOne, value: c.value, operand: "=" },
                { column: columnTwo, value: s.value, operand: "=" }
              ]
            });

            if (modelGroup.models.length === modelLength) {
              dispatch(modelGroupReducer.updateModelGroup(id, modelGroup, true));
            }
          });
        });
      });

      dataset.fields[columnTwo].data.forEach((c, i) => {
        dispatch(createRunFromValue(id, columnTwo, c.value)).then((val) => {
          modelGroup.models.push({
            job_id: val,
            model_name: "",
            conditions: [{ column: columnTwo, value: c.value, operand: "=" }]
          });

          if (modelGroup.models.length === modelLength) {
            dispatch(modelGroupReducer.updateModelGroup(id, modelGroup, true));
          }
        });
      });
    }
  };
}

export function createRunFromValue(runId, column, value) {
  return (dispatch, getState) => {
    const setup = setupReducer.getSetup(getState(), runId);

    const specialVals = [{ column: column, value: value, operand: "=" }];

    dispatch(setupReducer.createSetupFromSpecial(runId, specialVals));

    //return new Promise((resolve, reject) => resolve(0));

    return dispatch(
      setupReducer.initJobFromWizard(runId, setup.blueprintId, {
        isNoRedirect: true,
        skipBusinessCheck: true
      })
    );
  };
}

export function hasLoadedAllJobs(state, id) {
  let modelGroups = modelGroupReducer.getModelGroup(state, id);

  if (modelGroups !== undefined) {
    const jobKeys = getAllJobsFromOptimizer(state, id);

    let loadedCount = 0;

    jobKeys.forEach((k) => {
      if (Object.keys(runReducer.getJob(state, k)).length > 0) {
        loadedCount++;
      }
    });

    return loadedCount === jobKeys.length;
  }

  return false;
}

export function getAllJobsFromOptimizer(state, id) {
  let modelGroups = modelGroupReducer.getModelGroup(state, id);

  let jobKeys = [id];

  if (modelGroups !== undefined) {
    modelGroups.models.forEach((m) => jobKeys.push(m.job_id));
  }

  return jobKeys;
}

export function getAllJobsFromOptimizerAndInit(id) {
  return (dispatch, getState) => {
    let modelGroups = modelGroupReducer.getModelGroup(getState(), id);

    const setup = setupReducer.getSetup(getState(), id);

    let jobKeys = [id];

    if (modelGroups !== undefined && Object.keys(setup).length > 0) {
      modelGroups.models.forEach((m) => jobKeys.push(m.job_id));
    } else {
      setTimeout(() => dispatch(init(id)), 1000);

      return [];
    }

    return jobKeys;
  };
}

export function init(id) {
  return (dispatch, getState) => {
    const state = getState();

    const setup = setupReducer.getSetup(state, id);

    let modelGroups = modelGroupReducer.getModelGroup(getState(), setup.parentProjectId);

    if (
      setup &&
      setup.parentProjectId &&
      setup.parentProjectId !== undefined &&
      setup.parentProjectId !== id &&
      modelGroups !== undefined
    ) {
      dispatch(
        setOptimizer(setup.parentProjectId, {
          runId: id,
          budget: 10000000,
          drilldownValue:
            modelGroups.models.filter((m) => Number(m.job_id) === Number(id)).length > 0
              ? modelGroups.models.filter((m) => Number(m.job_id) === Number(id))[0].conditions[0].value
              : "Overall"
        })
      );

      dispatch(push("/run/" + setup.parentProjectId));
    } else {
      const optimizer = getOptimizer(state, id);

      if (optimizer === undefined) {
        dispatch(
          setOptimizer(id, {
            runId: id,
            budget: 10000000,
            drilldownValue: "Overall"
          })
        );
      }

      const jobKeys = dispatch(getAllJobsFromOptimizerAndInit(id));

      jobKeys.forEach((k) => {
        if (Object.keys(runReducer.getJob(getState(), k)).length === 0) {
          dispatch(runReducer.pollJob(k, true));
        }
      });
    }

    return Promise.resolve();
  };
}

export default optimizerReducer;
